import React, { useEffect, useRef } from "react";
import { matchPath, useLocation } from "react-router-dom";
import routes, { IRouteConfig } from "../routes/routes";

function allPaths(items: IRouteConfig[]): string[] {
  return items.reduce<string[]>((paths, route) => {
    paths.push(route.path);
    if (route.routes) paths.push(...allPaths(route.routes));
    return paths;
  }, []);
}

const templates = Array.from(new Set(allPaths(routes))).sort((a, b) => {
  const length = b.split("/").length - a.split("/").length;
  return length || (b.match(/[^/:]+/g) || []).length - (a.match(/[^/:]+/g) || []).length;
});

export function routeTemplate(pathname: string): string | null {
  return templates.find((path) => matchPath(pathname, { path, exact: true, strict: false })) || null;
}

export function safePublicOrigin(configuredUrl: string | undefined | null, parentOrigin: string): string | null {
  if (!configuredUrl || /[?#]/.test(configuredUrl)) return null;
  try {
    const url = new URL(configuredUrl);
    if (url.protocol !== "https:" || url.username || url.password || url.pathname !== "/") return null;
    return url.origin === parentOrigin ? parentOrigin : null;
  } catch {
    return null;
  }
}

interface Props { trackingId: string | undefined; publicOrigin?: string | null; }

const Analytics: React.FC<Props> = ({ trackingId, publicOrigin }) => {
  const location = useLocation();
  const validId = !!trackingId && /^G-[A-Z0-9]+$/.test(trackingId);
  const frame = useRef<HTMLIFrameElement>(null);
  const ready = useRef(false);
  const pending = useRef<string[]>([]);
  const lastLocation = useRef<string | undefined>();
  const safeOrigin = publicOrigin && safePublicOrigin(publicOrigin, publicOrigin);
  const enabled = validId && !!safeOrigin;

  useEffect(() => {
    if (!enabled) return;
    const key = location.key || `${location.pathname}${location.search}${location.hash}`;
    if (lastLocation.current === key) return;
    lastLocation.current = key;
    const path = routeTemplate(location.pathname);
    if (!path) return;
    if (ready.current && frame.current?.contentWindow) frame.current.contentWindow.postMessage({ type: "page_view", path }, safeOrigin!);
    else pending.current.push(path);
  }, [enabled, location, safeOrigin]);

  if (!enabled) return null;
  return <iframe
      ref={frame}
      title="Analytics"
      aria-hidden="true"
      tabIndex={-1}
      sandbox="allow-scripts allow-same-origin"
      referrerPolicy="no-referrer"
      src="/analytics-frame.html"
      onLoad={() => {
        const target = frame.current?.contentWindow;
        if (target) {
          target.postMessage({ type: "init", trackingId, publicOrigin: safeOrigin }, safeOrigin!);
          ready.current = true;
          pending.current.splice(0).forEach((path) => target.postMessage({ type: "page_view", path }, safeOrigin!));
        }
      }}
      style={{ position: "fixed", left: -9999, width: 1, height: 1, border: 0, pointerEvents: "none" }}
    />;
};

export default Analytics;
